// KPI strip — total opps, customers, AI subtotals, won/open/lost $, win rate.

import { classifyAI } from "../lib/aggregations";
import type { OpportunityRow } from "../types";

const fmtMoney = (n: number): string => {
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const sumAmount = (rows: OpportunityRow[]): number =>
  rows.reduce((acc, r) => acc + (r.amount ?? 0), 0);

export function renderKPIs(host: HTMLElement, rows: OpportunityRow[]): void {
  const total = rows.length;
  const accounts = new Set(rows.map((r) => r.account_id).filter(Boolean)).size;
  const won = rows.filter((r) => r.is_won === true);
  const lost = rows.filter((r) => r.is_closed === true && r.is_won === false);
  const open = rows.filter((r) => r.is_closed !== true);

  const wonAmount = sumAmount(won);
  const openAmount = sumAmount(open);
  // SGNL-225 — Lost $ tile dropped from the strip (was wrapping the
  // row onto a second line at 8 tiles + Win rate). `lost` array is
  // still computed for the win-rate denominator below.
  const closed = won.length + lost.length;
  const winRate = closed > 0 ? (won.length / closed) * 100 : 0;

  // SGNL-109 — Selling AI / Using AI subtotals as coloured KPI cards
  // (matches prototype). Hide a card when the class has zero opps in
  // the *full* current view — a permanently-$0 card would look broken
  // (today's corpus has no Selling-AI-classed opps at all because every
  // ai_boolean=true opp also has ai_enablement set, which classifyAI
  // resolves to "using" first).
  //
  // SGNL-224 — added Non-AI tile so Selling + Using + Non-AI ≡ Won $
  // (the tile to the right). Closes the reconciliation gap operators
  // had to do mentally before. Slate accent matches the Non-AI series
  // colour in the AI Breakdown chart.
  //
  // PR-Agent #142 importance 5: classify each row ONCE in a single
  // pass instead of three filter() calls. Pre-fix this was N*3
  // classifyAI() invocations on every render; post-fix it's N.
  // Also future-proofs against classifyAI becoming non-trivial —
  // the three buckets stay mutually consistent by construction.
  const sellingClass: OpportunityRow[] = [];
  const usingClass: OpportunityRow[] = [];
  const nonClass: OpportunityRow[] = [];
  for (const r of rows) {
    const cls = classifyAI(r);
    if (cls === "selling") sellingClass.push(r);
    else if (cls === "using") usingClass.push(r);
    else nonClass.push(r);
  }
  const sellingAiWon = sumAmount(sellingClass.filter((r) => r.is_won === true));
  const usingAiWon = sumAmount(usingClass.filter((r) => r.is_won === true));
  const nonAiWon = sumAmount(nonClass.filter((r) => r.is_won === true));

  // SGNL-70 — "Top-10 high-score open opps" tile. Average the
  // predicted_score (0–1) of the 10 highest-scoring OPEN opps in the
  // current view, shown as a 0–100 figure: a one-glance read on how
  // strong the best of the live pipeline looks. Only open opps carry a
  // score; nulls (unscored / closed) are excluded. Tile hidden when no
  // open opp is scored yet (mirrors the AI cards' zero-state hide).
  const scoredOpen = open
    .map((r) => r.predicted_score)
    .filter((s): s is number => s !== null && s !== undefined)
    .sort((a, b) => b - a);
  const top10 = scoredOpen.slice(0, 10);
  const top10Avg =
    top10.length > 0
      ? Math.round((top10.reduce((acc, s) => acc + s, 0) / top10.length) * 100)
      : null;
  // Label reflects the actual sample size — "Top-10" only when ≥10
  // open opps are scored, otherwise "Top-N" so the operator isn't
  // misled into thinking the average is over 10 deals when it's over
  // 3 (review LOW).
  const topScoreCard =
    top10Avg !== null
      ? `<div class="kpi kpi-accent-violet"><span class="kpi-label">Top-${top10.length} Score</span><span class="kpi-value" data-kpi="top10-score">${top10Avg}</span></div>`
      : "";

  const sellingCard = sellingClass.length > 0
    ? `<div class="kpi kpi-accent-aqua"><span class="kpi-label">Selling AI · Won $</span><span class="kpi-value" data-kpi="selling-ai-won">${fmtMoney(sellingAiWon)}</span></div>`
    : "";
  const usingCard = usingClass.length > 0
    ? `<div class="kpi kpi-accent-magenta"><span class="kpi-label">Using AI · Won $</span><span class="kpi-value" data-kpi="using-ai-won">${fmtMoney(usingAiWon)}</span></div>`
    : "";
  const nonCard = nonClass.length > 0
    ? `<div class="kpi kpi-accent-slate"><span class="kpi-label">Non-AI · Won $</span><span class="kpi-value" data-kpi="non-ai-won">${fmtMoney(nonAiWon)}</span></div>`
    : "";

  host.innerHTML = `
    <div class="kpi-strip" data-testid="kpis">
      <div class="kpi"><span class="kpi-label">Opportunities</span><span class="kpi-value" data-kpi="total">${total.toLocaleString()}</span></div>
      <div class="kpi"><span class="kpi-label">Customers</span><span class="kpi-value" data-kpi="accounts">${accounts.toLocaleString()}</span></div>
      ${sellingCard}
      ${usingCard}
      ${nonCard}
      <div class="kpi kpi-accent-violet"><span class="kpi-label">Won $</span><span class="kpi-value" data-kpi="won">${fmtMoney(wonAmount)}</span></div>
      <div class="kpi"><span class="kpi-label">Open $</span><span class="kpi-value" data-kpi="open">${fmtMoney(openAmount)}</span></div>
      <div class="kpi"><span class="kpi-label">Win rate</span><span class="kpi-value" data-kpi="win-rate">${winRate.toFixed(1)}%</span></div>
      ${topScoreCard}
    </div>
  `;
}
